import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

// Services
import { RequestService } from "services/RequestService";

function MetricCard({ title, value, unit, icon, color }) {
  return (
    <MDBox
      p={3}
      sx={{
        backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
        borderRadius: 3,
        boxShadow: ({ boxShadows: { md } }) => md,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            borderRadius="lg"
            sx={{
              backgroundColor: ({ palette }) => palette[color || "info"].main,
              color: ({ palette: { white } }) => white.main,
            }}
          >
            <Icon fontSize="medium">{icon}</Icon>
          </MDBox>
        </Grid>
        <Grid item xs>
          <MDBox ml={2}>
            <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
              {title}
            </MDTypography>
            <MDTypography variant="h4" fontWeight="bold">
              {value} {unit}
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  unit: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
};

function MetricView({ powerStationId, date }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    open: false,
  });

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await RequestService.get(`PowerStation/metric?powerStationId=${powerStationId}&date=${date}`);
      if (response.data && !response.data.error) {
        setMetrics(response.data.data);
      } else {
        setNotification({
          color: "error",
          content: "حدث خطأ أثناء جلب البيانات",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setNotification({
        color: "error",
        content: "حدث خطأ أثناء جلب البيانات",
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (powerStationId && date) {
      fetchMetrics();
    }
  }, [powerStationId, date]);

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <MDTypography variant="button" color="text">
          جاري التحميل...
        </MDTypography>
      </MDBox>
    );
  }

  if (!metrics) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <MDTypography variant="button" color="text">
          لا توجد بيانات متاحة
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="القدرة"
            value={metrics.capacity || 0}
            unit="MW"
            icon="electric_bolt"
            color="info"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="توليد الطاقة"
            value={metrics.powerGeneration || 0}
            unit="MW"
            icon="power"
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="الهدف"
            value={metrics.target || 0}
            unit="MW"
            icon="track_changes"
            color="warning"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="ضغط الغاز"
            value={metrics.gasPressure || 0}
            unit="bar"
            icon="compress"
            color="error"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="تدفق الغاز (م³/ساعة)"
            value={metrics.gasVolume || 0}
            unit="m³"
            icon="gas_meter"
            color="primary"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="التردد"
            value={metrics.frequency || 0}
            unit="Hz"
            icon="speed"
            color="secondary"
          />
        </Grid>
      </Grid>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.open}
        onClose={() => setNotification({ ...notification, open: false })}
        close={() => setNotification({ ...notification, open: false })}
      />
    </>
  );
}

MetricView.propTypes = {
  powerStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  date: PropTypes.string.isRequired,
};

export default MetricView;
